import { toTitleCaseUi } from "@/shared/utils/titleCase";
import clsx from "clsx";
import { Controller, type FieldPath, type FieldValues } from "react-hook-form";

import type { BaseInputProps } from "@/shared/types";
import { getThemeClass, getThemeComponentGroup } from "../lib/themeComponentClasses";
import { WrapperInput } from "./WrapperInput";

type TextInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = BaseInputProps<TFieldValues, TName> & {
  formatValueOnBlur?: (value: string) => string;
  formatValueOnChange?: (value: string) => string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  maxLength?: number;
  type?: React.HTMLInputTypeAttribute;
};

export const inputClassName =
  "h-9 w-full rounded-xl border px-4 text-[13px] outline-none transition disabled:cursor-not-allowed disabled:opacity-60";

export function TextInput<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  control,
  name,
  label,
  placeholder,
  description,
  disabled,
  formatValueOnBlur,
  formatValueOnChange,
  inputMode,
  maxLength,
  required,
  className,
  type = "text",
}: TextInputProps<TFieldValues, TName>) {
  const inputClasses = getThemeComponentGroup("input");
  const placeholderText = toTitleCaseUi(placeholder || "");

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <WrapperInput
          label={label}
          error={fieldState.error?.message}
          description={description}
          required={required}
          className={className}
        >
          <input
            {...field}
            id={field.name}
            data-testid={field.name}
            value={field.value ?? ""}
            type={type}
            inputMode={inputMode}
            maxLength={maxLength}
            placeholder={placeholderText}
            disabled={disabled}
            onChange={(event) => {
              if (!formatValueOnChange) {
                field.onChange(event);
                return;
              }

              field.onChange(formatValueOnChange(event.target.value));
            }}
            onBlur={(event) => {
              field.onBlur();

              if (!formatValueOnBlur) {
                return;
              }

              const formattedValue = formatValueOnBlur(event.target.value);

              if (formattedValue !== event.target.value) {
                field.onChange(formattedValue);
              }
            }}
            className={clsx(
              inputClassName,
              getThemeClass(inputClasses.borderColor, "border-theme-border-strong"),
              getThemeClass(inputClasses.bgColor, "bg-theme-surface"),
              getThemeClass(inputClasses.textColor, "text-theme-text"),
              getThemeClass(
                inputClasses.placeholderColor,
                "placeholder:text-theme-text-placeholder"
              ),
              getThemeClass(inputClasses.focusBorderColor, "focus:border-theme-primary"),
              "focus:ring-2",
              getThemeClass(inputClasses.focusRingColor, "focus:ring-theme-primary/10"),
              inputClasses.base,
              fieldState.error &&
                clsx(
                  inputClasses.errorBorderColor || "border-theme-danger-bright",
                  "focus:ring-theme-danger-bright/10"
                )
            )}
          />
        </WrapperInput>
      )}
    />
  );
}
